import type { RaumplanungAttributeMetadataListEntry } from 'atlas-shared';
import type { IndicatorFilter } from './IndicatorFilter';

/**
 * Per-leaf tag filter.
 *
 * In the "each leaf node is its own filter" model, every leaf of the filter
 * tree produces one {@link createTagFilter} instance that holds exactly one
 * category tag. The registry is the single source of truth for whether the
 * instance is active/pinned; `applies` simply checks whether a dataset belongs
 * to this tag (or to a child of it).
 */
export class HasTagFilter implements IndicatorFilter<unknown> {

    readonly id: string

    readonly label: string

    /** The single category tag this leaf filter stands for. */
    readonly tag: string

    /** Optional exclusive group; see {@link IndicatorFilter.exclusiveGroup}. */
    readonly exclusiveGroup?: string

    constructor(tag: string, exclusiveGroup?: string) {
        this.tag = tag
        this.id = tagFilterId(tag)
        this.label = tag
        this.exclusiveGroup = exclusiveGroup
    }

    /**
     * In the per-leaf model the registry is the single source of truth for
     * which filters are active; it only calls `applies` on active filters.
     * Therefore this always checks membership of `this.tag` (the inactive gate
     * is managed by the registry, not duplicated here).
     */
    applies(dataset: RaumplanungAttributeMetadataListEntry): boolean {
        return (dataset.tags ?? []).some(t => t === this.tag || t.startsWith(this.tag + " > "))
    }

    reset(): void {
        // Nothing to clear — the registry decides activeness via its own lists.
    }

    setConfig(_config: unknown): void {
        // A per-leaf filter has no configurable value besides its fixed tag.
    }
}

/**
 * The stable filter id for a tag filter. Single source of truth for the id
 * convention used both when creating a leaf filter and when resolving the
 * filter id associated with a tree leaf node.
 */
export function tagFilterId(tag: string): string {
    return `tag:${tag}`
}

/**
 * Creates a new per-leaf tag filter for `tag`. Each leaf of the filter tree
 * calls this once (and re-registers on tree reloads). `exclusiveGroup` is
 * optional and, when set, makes the filter mutually exclusive with other
 * filters of the same group (see {@link IndicatorFilter.exclusiveGroup}).
 */
export function createTagFilter(tag: string, exclusiveGroup?: string): HasTagFilter {
    return new HasTagFilter(tag, exclusiveGroup)
}